import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MessageCircle, Mail, MapPin, Clock, Send, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    investmentAmount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - replace with actual API call
    setTimeout(() => {
      toast({
        title: "Thank you for your interest!",
        description: "We'll contact you within 24 hours to discuss your investment requirements.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        investmentAmount: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            Start Your <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Investment Journey</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Connect with our investment experts to learn how IndexMaster can help you achieve your financial goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-blue-100">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-800">
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg cursor-pointer hover:bg-green-200 transition-colors">
                    <a href="https://wa.me/918088920016" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-5 h-5 text-green-600" />
                    </a>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">WhatsApp</h4>
                    <a href="https://wa.me/918088920016" target="_blank" rel="noopener noreferrer" 
                       className="text-slate-600 hover:text-green-600 transition-colors">
                      +91 80889 20016
                    </a>
                    <p className="text-sm text-slate-500 mt-1">Click to chat on WhatsApp</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Mail className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Email</h4>
                    <p className="text-slate-600">anuraag@compositeinvestments.com</p>
                    <p className="text-sm text-slate-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Office Address</h4>
                    <p className="text-slate-600 leading-relaxed">
                      K903, SNN Raj Lake View<br />
                      BTM Layout 2nd Stage<br />
                      Bangalore 560076, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Clock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">Business Hours</h4>
                    <p className="text-slate-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 2:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-gradient-to-br from-blue-500 to-green-500 text-white shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4">Why Investors Choose Us</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span>Experience</span>
                    <span className="font-bold">29+ Years</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Investment Approach</span>
                    <span className="font-bold">Rule-Based</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Risk Management</span>
                    <span className="font-bold">Dynamic</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Investment Horizon</span>
                    <span className="font-bold">Long-Term</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Services */}
            <div className="space-y-4">
              <Button 
                onClick={() => window.open('https://forms.gle/G7CPParsvDGe49Ck9', '_blank')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Partner with Us
              </Button>
              <Button 
                onClick={() => window.open('https://www.orbisonline.in/portal/account/login.aspx', '_blank')}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Client Login
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-white/80 backdrop-blur-sm shadow-2xl border-blue-100">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-slate-800">
                  Get Started with IndexMaster
                </CardTitle>
                <p className="text-slate-600 mt-2">
                  Fill out the form below and our investment advisors will contact you to discuss your portfolio requirements.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Enter your phone number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="investmentAmount" className="text-slate-700 font-medium">Investment Amount</Label>
                      <Input
                        id="investmentAmount"
                        name="investmentAmount"
                        type="text"
                        placeholder="e.g., ₹10 Lakhs"
                        value={formData.investmentAmount}
                        onChange={handleInputChange}
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 font-medium">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your investment goals and requirements..."
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group flex-1"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Submitting...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <Send className="mr-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          Submit Inquiry
                        </div>
                      )}
                    </Button>
                    <Button 
                      type="button"
                      onClick={() => window.open('https://forms.gle/ZfyxmjxEBEwmtEhs8', '_blank')}
                      className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group"
                    >
                      Open Account
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>

                  <p className="text-sm text-slate-500 text-center">
                    By submitting this form, you agree to our privacy policy and consent to be contacted by our investment advisors.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};